/**
 * MobileHeader.js
 *
 * A reusable mobile-native header bar that matches the look of the
 * native NavigationHeader.js in screens/.
 *
 * Usage:
 *   <MobileHeader title="Notices" onBack={() => navigate(-1)} />
 *   <MobileHeader title="Home" showBack={false} rightAction={<IconButton>...</IconButton>} />
 */
import React from 'react';
import { AppBar, Toolbar, IconButton, Typography, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import colors from '../common/Colors';

const MobileHeader = ({
  title = '',
  showBack = true,
  onBack,
  rightAction = null,
  backgroundColor = colors.BLUE,
  textColor = '#ffffff',
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleOpenDrawer = () => {
    if (window.NativeBridge && typeof window.NativeBridge.openDrawer === 'function') {
      window.NativeBridge.openDrawer();
    }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor,
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        /* Matches native NavigationHeader height */
        '& .MuiToolbar-root': {
          minHeight: '56px !important',
          paddingLeft: '4px',
          paddingRight: '8px',
        },
      }}
    >
      <Toolbar>
        {/* Back button — only when showBack is true */}
        {showBack ? (
          <IconButton
            edge="start"
            onClick={handleBack}
            sx={{
              color: textColor,
              mr: 0.5,
              padding: '8px',
              minHeight: 40,
              minWidth: 40,
            }}
            aria-label="back"
          >
            <ArrowBackIcon sx={{ fontSize: 22 }} />
          </IconButton>
        ) : (
          /* Hamburger menu — opens NativeDrawer when running inside WebView */
          <IconButton
            edge="start"
            onClick={handleOpenDrawer}
            sx={{
              color: textColor,
              mr: 0.5,
              padding: '8px',
              minHeight: 40,
              minWidth: 40,
            }}
            aria-label="menu"
          >
            <MenuIcon sx={{ fontSize: 24 }} />
          </IconButton>
        )}

        {/* Title — takes all available space */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            color: textColor,
            fontWeight: 600,
            fontSize: '17px',
            letterSpacing: 0.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>

        {/* Optional right-hand action (icon button, menu, etc.) */}
        {rightAction && (
          <Box sx={{ color: textColor, display: 'flex', alignItems: 'center' }}>
            {rightAction}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default MobileHeader;
